/**
 * SIL-1194: runs `task`, then holds its outcome - success OR failure - until
 * at least `minMs` has elapsed since the call started. A fast response never
 * flashes progress feedback (a spinner, "Building...") for less than the
 * minimum, and a slower response is never cut short: the wait is always
 * `Math.max(0, minMs - elapsed)`, never a fixed delay stacked on top of
 * however long `task` actually took.
 *
 * `clock` defaults to the real one; pass a fake in tests (see
 * `__tests__/minDuration.test.ts`) to verify the hold/no-hold behavior
 * without any real waiting.
 */
export interface MinDurationClock {
  now: () => number;
  sleep: (ms: number) => Promise<void>;
}

const REAL_CLOCK: MinDurationClock = {
  now: () => Date.now(),
  sleep: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
};

export async function withMinimumDuration<T>(
  task: () => Promise<T>,
  minMs: number,
  clock: MinDurationClock = REAL_CLOCK,
): Promise<T> {
  const start = clock.now();
  const settle = async (): Promise<void> => {
    const remaining = minMs - (clock.now() - start);
    if (remaining > 0) await clock.sleep(remaining);
  };
  try {
    const result = await task();
    await settle();
    return result;
  } catch (err) {
    await settle();
    throw err;
  }
}
